import { NetworkStatus, QueryHookOptions, useQuery } from '@apollo/client';
import {
  ChatMessageGroup,
  formatMessageData,
  groupMessagesByParticipantIdentity,
} from '@provision/chat-utils';
import { ConversationParticipationMode } from '@provision/conversation-types';
import { getFullName } from '@provision/formatting-utils';
import { useCallback, useMemo, useRef } from 'react';
import { config } from 'src/core/config';
import {
  ConversationParticipant,
  UseConversationParams,
} from 'src/graphql/entities/conversation';
import { mapMessagesForChatUI } from 'src/graphql/entities/conversation/conversation.mappings';
import { DEFAULT_MESSAGE_LIMIT } from 'src/graphql/entities/conversation-message/constants';
import { GET_CONVERSATION_MESSAGES_QUERY } from 'src/graphql/entities/conversation-message/conversation-message.operations';
import {
  ConversationParticipantItem,
  useGroupedConversationParticipants,
} from 'src/graphql/entities/conversation-participants';
import { useConversationParticipationRecords } from 'src/graphql/entities/conversation-participation/hooks/use-conversation-participation-records';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { ConditionInput, QueryVariables, Records } from 'src/graphql/types';
import { mergeFirstPageAndPersistPagination, useLoadMore } from 'src/graphql/utils';
import { useParticipationBadgeMap } from 'src/pages/Conversations/Chat/utils';
import { ConversationMessageRaw } from 'src/types';
import { isSameRecords } from 'src/utils/objects';

export type GetConversationMessageQuery = {
  getConversationMessage: Records<ConversationMessageRaw>;
};

type UseConversationMessageParams = UseConversationParams & {
  user: ReturnType<typeof useCurrentUserProfile>['user'];
  options?: QueryHookOptions<GetConversationMessageQuery>;
};

const EMPTY_PARTICIPANTS: ConversationParticipantItem[] = [];

export const useConversationMessages = (params: UseConversationMessageParams) => {
  const { conversationId, limit, options, user } = params;

  const messagesCacheRef = useRef<Record<string, ChatMessageGroup<ConversationParticipant>[]>>(
    {},
  );

  const conversationQueryFilters = useMemo(() => {
    const filters = [];

    if (conversationId) {
      filters.push({
        attribute: 'conversationId',
        operator: '=',
        value: conversationId,
      });
    }

    return filters;
  }, [conversationId]);

  const skip = !conversationQueryFilters.length || !conversationId || options?.skip;

  const { conversationParticipations } = useConversationParticipationRecords({
    conversationId: conversationId ?? '',
    skip,
    includeDeleted: true,
  });

  const {
    data,
    client,
    fetchMore,
    loading: messagesLoading,
    variables,
    networkStatus,
    ...rest
  } = useQuery<GetConversationMessageQuery, QueryVariables>(GET_CONVERSATION_MESSAGES_QUERY, {
    variables: getConversationMessagesQueryVariables({
      filters: conversationQueryFilters,
      limit,
    }),
    notifyOnNetworkStatusChange: true,
    ...options,
    skip,
  });

  const currentOffset = data?.getConversationMessage.metadata?.offset ?? 0;

  // The cacheRef helps to avoid UI blinking when conversation participants change
  // as any participation change updates query parameters, and the query starts
  // to returns nothing (as nothing is cached for such a filter combination).
  type ParticipantsCache = {
    groupedParticipants: Record<ConversationParticipationMode, ConversationParticipantItem[]>;
    participants: ConversationParticipantItem[];
  };

  const cacheRef = useRef<ParticipantsCache | undefined>(undefined);

  const { groupedParticipants: rawGroup, participants: rawList } =
    useGroupedConversationParticipants(conversationParticipations);

  if (rawList?.length) {
    cacheRef.current = { groupedParticipants: rawGroup, participants: rawList };
  }

  const groupedParticipants = useMemo(
    () =>
      (rawList?.length ? rawGroup : cacheRef.current?.groupedParticipants) ?? {
        [ConversationParticipationMode.Assist]: [],
        [ConversationParticipationMode.Engage]: [],
      },
    [rawList, rawGroup],
  );

  /*
    A fresh `[]` here is a new identity every render, and consumers memoize off this
    array (useSelectedParticipants) — while participants are still loading that turns
    into an unbounded render/setState loop that trips React's update-depth limit.
  */
  const participants =
    (rawList?.length ? rawList : cacheRef.current?.participants) ?? EMPTY_PARTICIPANTS;

  const badgeMap = useParticipationBadgeMap();

  const messages = useMemo(() => {
    if (!conversationId) {
      return [];
    }

    const messages = (data?.getConversationMessage.records ?? []).map((message) => ({
      ...message,
      messageDispatches: message.messageDispatches
        .map((dispatch) => ({
          ...dispatch,
          participation: conversationParticipations.find(
            (participation) => participation.id === dispatch.conversationParticipationId,
          )!,
        }))
        .filter((i) => i.participation !== undefined),
    }));

    if (!messages && messagesCacheRef.current[conversationId]?.length) {
      return messagesCacheRef.current[conversationId];
    }

    const participantsMap = Object.values(groupedParticipants)
      .flat()
      .reduce(
        (acc, p) => {
          acc[p.conversationParticipantId] = p.client || p.platformUser || p.rsmEmail;
          return acc;
        },
        // This is needed to immediately show current user
        // info in sent messages for conversations where they weren't
        // a participant yet.
        user
          ? {
              [user.userId]: {
                ...user,
                isActive: true,
                phoneNumber: '',
                avatarFileKey: user.avatarFileKey,
                fullName: getFullName(user),
              } as ConversationParticipant,
            }
          : ({} as Record<string, ConversationParticipant>),
      );

    const newMessages = groupMessagesByParticipantIdentity<
      ReturnType<typeof formatMessageData>,
      ConversationParticipant
    >({
      conversationId,
      messages,
      baseUrl: config.s3AttachmentsUrl,
      // @ts-expect-error figure out what's wrong with participant typings
      formatMessage: formatMessageData,
      participants: participantsMap,
    });

    const inactiveUserIds = groupedParticipants[ConversationParticipationMode.Assist]
      .filter((p) => p.platformUser && !p.platformUser.isActive)
      .map((p) => p.participantIdentity);

    const mappedMessages = mapMessagesForChatUI({
      conversationId,
      items: newMessages,
      conversationParticipants: {
        engage: groupedParticipants[ConversationParticipationMode.Engage],
        assist: groupedParticipants[ConversationParticipationMode.Assist],
      },
      badgeMap,
      inactiveUserIds,
    });

    messagesCacheRef.current[conversationId] = mappedMessages;

    return mappedMessages;
  }, [
    user,
    conversationParticipations,
    conversationId,
    groupedParticipants,
    data?.getConversationMessage.records,
    badgeMap,
  ]);

  const { loadMore: loadMoreMessages, hasReachedEnd } =
    useLoadMore<GetConversationMessageQuery>({
      offset: data?.getConversationMessage?.records?.length ?? 0,
      fetchMore,
      params: variables?.params!,
      deduplicationPredicate: isSameRecords,
    });

  // Prevent loading more messages if previous request hasn't been completed yet.
  const loadMore = useCallback<typeof loadMoreMessages>(
    (...args) => {
      if (messagesLoading) {
        return Promise.resolve();
      }
      return loadMoreMessages(...args);
    },
    [loadMoreMessages, messagesLoading],
  );

  /*
    Merges a fresh newest page into the loaded thread, for messages that arrived after it
    was cached. `ttl: 0` skips the response cache link, which would otherwise hand back
    the very page that is missing them.
  */
  const reloadNewestPage = useCallback(async (): Promise<string[]> => {
    const { data: freshData } = await client.query<
      GetConversationMessageQuery,
      QueryVariables
    >({
      query: GET_CONVERSATION_MESSAGES_QUERY,
      variables,
      fetchPolicy: 'no-cache',
      context: { ttl: 0 },
    });

    /*
      Only the ids this merge adds count: one the cache already held is no news to its
      watchers, so the thread will not re-render for it.
    */
    let addedIds: string[] = [];
    client.cache.updateQuery<GetConversationMessageQuery, QueryVariables>(
      { query: GET_CONVERSATION_MESSAGES_QUERY, variables },
      (prev) => {
        if (!prev) return prev;
        const cachedIds = new Set(
          prev.getConversationMessage.records.map((record) => record.conversationMessageId),
        );
        addedIds = freshData.getConversationMessage.records
          .map((record) => record.conversationMessageId)
          .filter((id) => !cachedIds.has(id));
        return mergeFirstPageAndPersistPagination(prev, freshData);
      },
    );

    return addedIds;
  }, [client, variables]);

  const totalRecords = data?.getConversationMessage.metadata?.totalRecords ?? 0;
  const loadedCount = data?.getConversationMessage?.records?.length ?? 0;
  /*
    This query always asks for totalRecords to be skipped, so the count is never there
    to compare against and the fallback below decides. A first page the server could
    not fill means the whole history is already here — without this check hasMore stays
    optimistically true for every short conversation until something triggers a probe
    fetch that comes back empty.
  */
  const isFirstPageUnfilled =
    !messagesLoading && loadedCount < (limit ?? DEFAULT_MESSAGE_LIMIT);

  return {
    participants,
    messages,
    client,
    hasMore: data?.getConversationMessage.metadata?.totalRecords
      ? totalRecords > loadedCount
      : !hasReachedEnd && !isFirstPageUnfilled,
    offset: currentOffset,
    loadMore,
    reloadNewestPage,
    // if there are already some messages we don't show loading state
    // as it will produce unwanted re-renders
    loading: !messages?.length && messagesLoading,
    isLoadingMore: networkStatus === NetworkStatus.fetchMore,
    ...rest,
  };
};

export function getConversationMessagesQueryVariables({
  filters,
  limit,
  offset,
}: {
  filters: ConditionInput[];
  limit?: number;
  offset?: number;
}): QueryVariables {
  return {
    params: {
      // Replies are loaded with the message they reply to, never as timeline items.
      filters: [
        ...filters,
        { attribute: 'parentConversationMessageId', operator: 'is_null', value: '' },
      ],
      offset: offset ?? 0,
      limit: limit ?? DEFAULT_MESSAGE_LIMIT,
      sorting: { attribute: 'createdAt', desc: true },
      includeDeleted: true,
    },
    skipTotalRecords: true,
  };
}
